import { useMutation } from '@apollo/client';
import React, { useRef } from 'react';
import { animated } from 'react-spring';
import styled from 'styled-components';
import { createLeadMutation } from '../../../../client/__graphql__/mutations';
import { SiteContext } from '../../../utils/context/SiteContext';
import { useFadeTransition } from '../../../utils/hooks/useFadeTransition';
import { useForm } from '../../../utils/hooks/useForm';
import { useFormTransition } from '../../../utils/hooks/useFormTransition';
import { Seo } from '../../../utils/seo/Index';
import { createGTMEvent } from '../../../utils/tracking/gtm';
import { ILeadInput, ILeadResponse } from '../../../__types__/global';
import { Container } from '../../layout/Container';
import { Section } from '../../layout/Section';
import { Thanks } from '../../layout/Thanks';
import { AccordionBenefits } from '../../partials/AccordionBenefits';
import { AccordionGeneral } from '../../partials/AccordionGeneral';
import { Button } from '../../ui/button/Button';
import { ButtonGroup } from '../../ui/button/ButtonGroup';
import { Form, FormRow } from '../../ui/form/Form';
import { FormError } from '../../ui/form/FormError';
import { Input } from '../../ui/form/Input';
import { ButtonLink } from '../../ui/link/ButtonLink';
import { Modal } from '../../ui/modal/Modal';
import { PlayButton } from '../../ui/play-button/Index';
import { Heading } from '../../ui/text/Heading';
import { Paragraph } from '../../ui/text/Paragraph';
import { VideoPlayer } from '../../ui/video-player/Index';
import { useDigtectiveContext } from '../../../__lib__/digtective/context/hooks/useDigtectiveContext';

const Klikk: React.FC = () => {
  const { routeConfig }: any = React.useContext(SiteContext);
  const readMoreRef: any = useRef();
  const contactRef: any = useRef();

  const { submitWithDigger } = useDigtectiveContext();

  const { fadeIn: pageFadeIn } = useFadeTransition({
    immidiate: true
  });
  const [showVideo, setShowVideo] = React.useState(false);
  const [posted, setPosted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const readMoreClick = () => {
    const top = readMoreRef.current.offsetTop - 120;
    window.scroll({ top: top, behavior: 'smooth' });
  };
  const contactClick = () => {
    const top = contactRef.current.offsetTop - 120;
    window.scroll({ top: top, behavior: 'smooth' });
  };

  const VALUATION_MUTATION = createLeadMutation({
    valuation: {
      response: `{
        success
        data {
          id
          address
          zip
          phone
          email
        }
      }`
    }
  });

  const [lead, { error }] = useMutation<ILeadResponse, ILeadInput>(
    VALUATION_MUTATION,
    {
      onCompleted: ({ lead }) => {
        setLoading(false);
        if (lead.valuation?.success) {
          setPosted(true);

          // GTM tracking
          createGTMEvent({
            event: 'pmFormSubmission',
            gaEvent: 'Verdivurdering NY',
            gaCategory: 'Form',
            gaAction: 'Submit'
          });
        }
      }
    }
  );

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'address',
        placeholder: 'Eks. Adresseveien 23',
        value: '',
        label: 'Adresse'
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 1234',
        value: '',
        label: 'Postnummer *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);

      setLoading(true);
      submitWithDigger({ zip: preparedFields.zip }, ({ diggerId }) => {
        lead({
          variables: {
            input: {
              ...preparedFields,
              diggerId,
              referer: `${location.pathname}${location.search}`,
              leadType: 'VERDIVURDERING',
              pageType: 'GENERELL'
            }
          }
        });
      });
    }
  });

  return (
    <>
      <Seo
        title="Klikk - PrivatMegleren - Nordeas Eiendomsmeglerkjede"
        description="Markedsføring av bolig, KLIKK X smart bolig annonseteknologi"
        image="https://cdn.reeltime.no/pm_assets/img/bakgrunner/pmfront.jpg"
        imageTitle="/markedsføring-av-bolig-klikk-x-smart-bolig-annonsering-salg"
      />
      <Container style={{ lineHeight: 1.6 }}>
        <Section style={{ paddingTop: '0' }} className="top-section">
          <div className="container" style={{ marginTop: '150px' }}>
            <h1 style={{ textAlign: 'center', marginBottom: '20px' }}>
              Få flere budgivere med KLIKK X
            </h1>
            {/* <KlikkX>
              <svg
                id=""
                                role="img"
                className="klikk_logo_klikk"
                aria-labelledby="klikk_logo_klikk"
                viewBox="0 0 1650 300"
                height="80px"
                width="100%"
                xmlns="http://www.w3.org/2000/svg"
                text-anchor="middle"
              >
                <linearGradient id="grad1" x1="10%" y1="30%" x2="80%" y2="80%">
                  <stop
                    offset="0%"
                    style={{ stopColor: 'rgb(193,126,84)', stopOpacity: 1 }}
                  ></stop>
                  <stop
                    offset="50%"
                    style={{ stopColor: 'rgb(241,192,135)', stopOpacity: 1 }}
                  ></stop>
                  <stop
                    offset="100%"
                    style={{ stopColor: 'rgb(185,113,73)', stopOpacity: 1 }}
                  ></stop>
                </linearGradient>
                <text
                  id="klikk_logo_text_k"
                  className="klikk_text"
                  y="290"
                  x="800"
                  fill="url(#grad1)"
                  style={{ fontSize: '380px', fontWeight: 'bold' }}
                >
                  KLiKK X
                </text>
              </svg>
            </KlikkX> */}
            <p style={{ textAlign: 'center' }}>
              Siden 2011 har vi arbeidet med teknologisk utvikling for å kunne
              tilby det beste og smarteste annonseproduktet til eiendomsselgere.
            </p>
            <p style={{ textAlign: 'center' }}>
              Skreddersydd annonsering tilpasset mottaker, basert på kunstig
              intelligens og maskinlæring, gir i snitt kortere salgstid.
            </p>
          </div>
          <FakeGraph>
            <svg
              width="412"
              height="412"
              viewBox="0 0 212 212"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M124.475 21.4391L135.912 40.1504L136.422 40.9839L137.39 40.8549L159.128 37.9591L161.314 59.7799L161.412 60.752L162.34 61.0559L183.182 67.8786L175.684 88.487L175.35 89.405L176.054 90.0817L191.872 105.272L176.175 120.586L175.475 121.268L175.817 122.183L183.477 142.732L162.69 149.719L161.764 150.03L161.674 151.003L159.66 172.84L137.9 170.116L136.93 169.995L136.428 170.832L125.138 189.633L106.715 177.737L105.894 177.207L105.078 177.744L86.749 189.785L75.3117 171.073L74.8021 170.24L73.8338 170.369L52.0958 173.265L49.9097 151.444L49.8123 150.472L48.8838 150.168L28.0422 143.345L35.5402 122.737L35.8742 121.819L35.1696 121.142L19.3522 105.952L35.0493 90.6378L35.7485 89.9556L35.4073 89.0402L27.747 68.4916L48.5341 61.5047L49.4602 61.1935L49.5499 60.2207L51.5639 38.3834L73.324 41.1076L74.2934 41.229L74.7963 40.3914L86.0857 21.5905L104.509 33.4863L105.33 34.0162L106.146 33.4798L124.475 21.4391Z"
                stroke="white"
                strokeWidth="3"
              />
              <path
                d="M55.9792 140.939V137.73H55.3948V137.241H55.9792V136.926C55.9792 136.514 56.0966 136.193 56.3313 135.963C56.5683 135.732 56.9021 135.617 57.3328 135.617C57.588 135.617 57.9104 135.687 58.3 135.826L58.1941 136.359C57.9093 136.245 57.6381 136.188 57.3806 136.188C57.1095 136.188 56.9135 136.25 56.7927 136.373C56.6742 136.493 56.615 136.676 56.615 136.92V137.241H57.3704V137.73H56.615V140.939H55.9792ZM58.6145 140.939H57.9788V137.241H58.6145V140.939ZM60.2415 137.241L60.262 137.706C60.5445 137.35 60.9137 137.172 61.3694 137.172C62.151 137.172 62.5452 137.613 62.552 138.495V140.939H61.9197V138.492C61.9174 138.225 61.8559 138.028 61.7351 137.901C61.6166 137.773 61.4309 137.709 61.178 137.709C60.9729 137.709 60.7929 137.764 60.6379 137.873C60.483 137.983 60.3622 138.126 60.2756 138.304V140.939H59.6433V137.241H60.2415ZM64.1038 137.241L64.1243 137.706C64.4068 137.35 64.776 137.172 65.2317 137.172C66.0133 137.172 66.4075 137.613 66.4143 138.495V140.939H65.782V138.492C65.7797 138.225 65.7182 138.028 65.5974 137.901C65.4789 137.773 65.2932 137.709 65.0403 137.709C64.8352 137.709 64.6552 137.764 64.5002 137.873C64.3453 137.983 64.2245 138.126 64.1379 138.304V140.939H63.5056V137.241H64.1038ZM67.3816 140.608C67.3816 140.498 67.4135 140.407 67.4773 140.334C67.5434 140.261 67.6414 140.225 67.7712 140.225C67.9011 140.225 67.9991 140.261 68.0652 140.334C68.1335 140.407 68.1677 140.498 68.1677 140.608C68.1677 140.712 68.1335 140.8 68.0652 140.871C67.9991 140.941 67.9011 140.977 67.7712 140.977C67.6414 140.977 67.5434 140.941 67.4773 140.871C67.4135 140.8 67.3816 140.712 67.3816 140.608ZM69.8118 137.241L69.8323 137.706C70.1148 137.35 70.484 137.172 70.9397 137.172C71.7213 137.172 72.1155 137.613 72.1223 138.495V140.939H71.49V138.492C71.4877 138.225 71.4262 138.028 71.3054 137.901C71.1869 137.773 71.0012 137.709 70.7483 137.709C70.5432 137.709 70.3632 137.764 70.2083 137.873C70.0533 137.983 69.9325 138.126 69.8459 138.304V140.939H69.2136V137.241H69.8118ZM72.9084 139.056C72.9084 138.693 72.9791 138.368 73.1204 138.078C73.2639 137.789 73.4622 137.566 73.7151 137.408C73.9703 137.251 74.2608 137.172 74.5867 137.172C75.0903 137.172 75.497 137.347 75.8069 137.695C76.1191 138.044 76.2751 138.508 76.2751 139.087V139.131C76.2751 139.491 76.2056 139.815 76.0667 140.102C75.9299 140.387 75.7328 140.609 75.4753 140.768C75.2201 140.928 74.9262 141.007 74.5935 141.007C74.0922 141.007 73.6855 140.833 73.3733 140.484C73.0634 140.136 72.9084 139.674 72.9084 139.1V139.056ZM73.5442 139.131C73.5442 139.541 73.6388 139.87 73.8279 140.119C74.0193 140.367 74.2745 140.491 74.5935 140.491C74.9148 140.491 75.17 140.366 75.3591 140.115C75.5483 139.862 75.6428 139.509 75.6428 139.056C75.6428 138.65 75.546 138.322 75.3523 138.071C75.1609 137.818 74.9057 137.692 74.5867 137.692C74.2745 137.692 74.0227 137.816 73.8313 138.065C73.6399 138.313 73.5442 138.668 73.5442 139.131Z"
                fill="white"
              />
              <path
                d="M49.9392 128.939C49.9392 127.282 51.2824 125.939 52.9392 125.939H78.9392C80.5961 125.939 81.9392 127.282 81.9392 128.939V132.939H49.9392V128.939Z"
                fill="#E8C893"
              />
              <path
                d="M89.7991 138.625L89.1907 139.257V140.939H88.5344V135.963H89.1907V138.423L91.4021 135.963H92.1951L90.2366 138.16L92.3489 140.939H91.5627L89.7991 138.625ZM93.5828 140.402H95.9412V140.939H92.9231V135.963H93.5828V140.402ZM97.3938 140.939H96.7375V135.963H97.3938V140.939ZM99.8616 138.625L99.2532 139.257V140.939H98.5969V135.963H99.2532V138.423L101.465 135.963H102.258L100.299 138.16L102.411 140.939H101.625L99.8616 138.625ZM104.25 138.625L103.642 139.257V140.939H102.986V135.963H103.642V138.423L105.853 135.963H106.646L104.688 138.16L106.8 140.939H106.014L104.25 138.625ZM110.734 137.87L111.927 135.963H112.699L111.127 138.43L112.737 140.939H111.958L110.734 138.998L109.504 140.939H108.728L110.341 138.43L108.765 135.963H109.534L110.734 137.87Z"
                fill="white"
              />
              <path
                d="M83.9392 128.939C83.9392 127.282 85.2824 125.939 86.9392 125.939H112.939C114.596 125.939 115.939 127.282 115.939 128.939V132.939H83.9392V128.939Z"
                fill="#E8C893"
              />
              <path
                d="M83.9392 101.939C83.9392 100.282 85.2824 98.9391 86.9392 98.9391H112.939C114.596 98.9391 115.939 100.282 115.939 101.939V132.939H83.9392V101.939Z"
                fill="#E8C893"
              />
              <path
                d="M119.433 140.939H118.777V135.963H119.433V140.939ZM121.57 139.227L121.173 139.64V140.939H120.541V135.689H121.173V138.864L121.511 138.458L122.663 137.241H123.432L121.993 138.786L123.6 140.939H122.858L121.57 139.227ZM125.117 139.227L124.721 139.64V140.939H124.089V135.689H124.721V138.864L125.059 138.458L126.211 137.241H126.98L125.541 138.786L127.148 140.939H126.406L125.117 139.227ZM129.099 141.007C128.598 141.007 128.19 140.843 127.876 140.515C127.561 140.185 127.404 139.744 127.404 139.193V139.076C127.404 138.709 127.474 138.382 127.613 138.095C127.754 137.806 127.95 137.58 128.2 137.419C128.453 137.255 128.727 137.172 129.021 137.172C129.502 137.172 129.875 137.331 130.142 137.648C130.408 137.964 130.542 138.418 130.542 139.008V139.271H128.036C128.045 139.636 128.151 139.931 128.354 140.156C128.559 140.38 128.819 140.491 129.134 140.491C129.357 140.491 129.546 140.446 129.701 140.355C129.856 140.263 129.991 140.143 130.108 139.992L130.494 140.293C130.184 140.769 129.719 141.007 129.099 141.007ZM129.021 137.692C128.766 137.692 128.551 137.785 128.378 137.972C128.205 138.157 128.098 138.417 128.057 138.752H129.909V138.704C129.891 138.382 129.805 138.134 129.65 137.959C129.495 137.781 129.285 137.692 129.021 137.692ZM136.144 139.131C136.144 139.696 136.014 140.151 135.754 140.495C135.494 140.837 135.146 141.007 134.708 141.007C134.241 141.007 133.88 140.842 133.625 140.512L133.594 140.939H133.013V135.689H133.645V137.648C133.9 137.331 134.253 137.172 134.701 137.172C135.15 137.172 135.502 137.342 135.758 137.682C136.015 138.021 136.144 138.486 136.144 139.076V139.131ZM135.511 139.059C135.511 138.629 135.428 138.296 135.262 138.061C135.096 137.826 134.856 137.709 134.544 137.709C134.127 137.709 133.828 137.903 133.645 138.29V139.89C133.839 140.277 134.141 140.471 134.551 140.471C134.854 140.471 135.09 140.353 135.259 140.119C135.427 139.884 135.511 139.531 135.511 139.059ZM136.776 139.056C136.776 138.693 136.847 138.368 136.988 138.078C137.132 137.789 137.33 137.566 137.583 137.408C137.838 137.251 138.128 137.172 138.454 137.172C138.958 137.172 139.365 137.347 139.675 137.695C139.987 138.044 140.143 138.508 140.143 139.087V139.131C140.143 139.491 140.073 139.815 139.934 140.102C139.798 140.387 139.601 140.609 139.343 140.768C139.088 140.928 138.794 141.007 138.461 141.007C137.96 141.007 137.553 140.833 137.241 140.484C136.931 140.136 136.776 139.674 136.776 139.1V139.056ZM137.412 139.131C137.412 139.541 137.506 139.87 137.696 140.119C137.887 140.367 138.142 140.491 138.461 140.491C138.782 140.491 139.038 140.366 139.227 140.115C139.416 139.862 139.51 139.509 139.51 139.056C139.51 138.65 139.414 138.322 139.22 138.071C139.029 137.818 138.773 137.692 138.454 137.692C138.142 137.692 137.89 137.816 137.699 138.065C137.508 138.313 137.412 138.668 137.412 139.131ZM141.623 140.939H140.99V135.689H141.623V140.939ZM143.325 140.939H142.693V137.241H143.325V140.939ZM142.641 136.26C142.641 136.157 142.672 136.071 142.734 136C142.797 135.929 142.891 135.894 143.014 135.894C143.137 135.894 143.23 135.929 143.294 136C143.358 136.071 143.39 136.157 143.39 136.26C143.39 136.362 143.358 136.448 143.294 136.516C143.23 136.585 143.137 136.619 143.014 136.619C142.891 136.619 142.797 136.585 142.734 136.516C142.672 136.448 142.641 136.362 142.641 136.26ZM144.19 139.059C144.19 138.483 144.323 138.025 144.59 137.685C144.856 137.343 145.209 137.172 145.649 137.172C146.1 137.172 146.452 137.332 146.705 137.651L146.736 137.241H147.314V140.85C147.314 141.329 147.171 141.706 146.886 141.982C146.604 142.257 146.223 142.395 145.745 142.395C145.478 142.395 145.217 142.338 144.962 142.224C144.707 142.11 144.512 141.954 144.378 141.756L144.706 141.377C144.977 141.712 145.309 141.879 145.7 141.879C146.008 141.879 146.247 141.792 146.418 141.619C146.591 141.446 146.678 141.202 146.678 140.888V140.57C146.425 140.862 146.08 141.007 145.642 141.007C145.209 141.007 144.858 140.833 144.59 140.484C144.323 140.136 144.19 139.661 144.19 139.059ZM144.825 139.131C144.825 139.548 144.911 139.876 145.082 140.115C145.253 140.352 145.492 140.471 145.8 140.471C146.198 140.471 146.491 140.29 146.678 139.927V138.239C146.484 137.886 146.194 137.709 145.806 137.709C145.499 137.709 145.258 137.829 145.085 138.068C144.912 138.307 144.825 138.662 144.825 139.131ZM149.587 139.083H147.919V138.567H149.587V139.083ZM125.849 147.227L125.452 147.64V148.939H124.82V143.689H125.452V146.864L125.791 146.458L126.943 145.241H127.712L126.273 146.786L127.879 148.939H127.137L125.849 147.227ZM129.017 145.241V149.366C129.017 150.077 128.695 150.433 128.05 150.433C127.911 150.433 127.782 150.412 127.664 150.371V149.865C127.737 149.884 127.832 149.893 127.951 149.893C128.092 149.893 128.199 149.854 128.272 149.776C128.347 149.701 128.385 149.569 128.385 149.38V145.241H129.017ZM128.32 144.26C128.32 144.16 128.351 144.074 128.412 144.004C128.476 143.931 128.568 143.894 128.689 143.894C128.812 143.894 128.906 143.929 128.969 144C129.033 144.071 129.065 144.157 129.065 144.26C129.065 144.362 129.033 144.448 128.969 144.516C128.906 144.585 128.812 144.619 128.689 144.619C128.566 144.619 128.474 144.585 128.412 144.516C128.351 144.448 128.32 144.362 128.32 144.26ZM129.872 147.056C129.872 146.693 129.941 146.369 130.08 146.082C130.222 145.792 130.42 145.569 130.675 145.412C130.933 145.252 131.224 145.172 131.55 145.172C131.801 145.172 132.033 145.22 132.247 145.316L132.497 144.81H132.921L132.572 145.511C133.016 145.862 133.239 146.402 133.239 147.131C133.239 147.491 133.168 147.816 133.027 148.105C132.885 148.394 132.687 148.618 132.432 148.775C132.177 148.93 131.885 149.007 131.557 149.007C131.32 149.007 131.103 148.968 130.907 148.888L130.655 149.397H130.231L130.573 148.703C130.105 148.364 129.872 147.815 129.872 147.056ZM130.504 147.131C130.504 147.573 130.615 147.921 130.836 148.173L132.008 145.801C131.867 145.728 131.714 145.692 131.55 145.692C131.236 145.692 130.983 145.816 130.791 146.065C130.6 146.313 130.504 146.668 130.504 147.131ZM132.603 147.056C132.603 146.641 132.504 146.307 132.305 146.054L131.143 148.406C131.269 148.463 131.406 148.491 131.557 148.491C131.874 148.491 132.127 148.367 132.316 148.119C132.507 147.868 132.603 147.532 132.603 147.11V147.056ZM137.128 147.131C137.128 147.694 136.999 148.147 136.742 148.491C136.484 148.835 136.136 149.007 135.696 149.007C135.247 149.007 134.894 148.865 134.636 148.58V150.361H134.004V145.241H134.582L134.613 145.651C134.87 145.332 135.228 145.172 135.686 145.172C136.13 145.172 136.481 145.34 136.739 145.675C136.998 146.01 137.128 146.476 137.128 147.073V147.131ZM136.496 147.059C136.496 146.642 136.407 146.313 136.229 146.071C136.052 145.83 135.808 145.709 135.498 145.709C135.115 145.709 134.828 145.879 134.636 146.218V147.985C134.826 148.323 135.115 148.491 135.505 148.491C135.808 148.491 136.048 148.372 136.226 148.132C136.406 147.891 136.496 147.533 136.496 147.059ZM139.469 149.007C138.968 149.007 138.56 148.843 138.246 148.515C137.931 148.185 137.774 147.744 137.774 147.193V147.076C137.774 146.709 137.844 146.382 137.983 146.095C138.124 145.806 138.32 145.58 138.571 145.419C138.823 145.255 139.097 145.172 139.391 145.172C139.872 145.172 140.245 145.331 140.512 145.648C140.779 145.964 140.912 146.418 140.912 147.008V147.271H138.406C138.416 147.636 138.522 147.931 138.724 148.156C138.929 148.38 139.189 148.491 139.504 148.491C139.727 148.491 139.916 148.446 140.071 148.355C140.226 148.263 140.362 148.143 140.478 147.992L140.864 148.293C140.554 148.769 140.089 149.007 139.469 149.007ZM139.391 145.692C139.136 145.692 138.921 145.785 138.748 145.972C138.575 146.157 138.468 146.417 138.427 146.752H140.28V146.704C140.261 146.382 140.175 146.134 140.02 145.959C139.865 145.781 139.655 145.692 139.391 145.692ZM143.434 145.808C143.339 145.792 143.235 145.784 143.123 145.784C142.709 145.784 142.427 145.961 142.279 146.314V148.939H141.647V145.241H142.262L142.272 145.668C142.48 145.338 142.774 145.172 143.154 145.172C143.277 145.172 143.371 145.188 143.434 145.22V145.808Z"
                fill="white"
              />
              <path
                d="M117.939 56.9391C117.939 55.2822 119.282 53.9391 120.939 53.9391H146.939C148.596 53.9391 149.939 55.2822 149.939 56.9391V132.939H117.939V56.9391Z"
                fill="#E8C893"
              />
              <path
                d="M117.939 128.939C117.939 127.282 119.282 125.939 120.939 125.939H146.939C148.596 125.939 149.939 127.282 149.939 128.939V132.939H117.939V128.939Z"
                fill="#E8C893"
              />
            </svg>
          </FakeGraph>
        </Section>
        <Section className="section">
          <div className="container">
            <Texts>
              <div className="text-column padding-bottom">
                <p>
                  Skreddersydd annonsering tilpasset mottaker, basert på kunstig
                  intelligens og maskinlæring, gir i snitt kortere salgstid.
                </p>
                <div className="svg-icon center">
                  <svg
                    width="142"
                    height="85"
                    viewBox="0 0 142 85"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M114.589 14.1029L104.084 3.59746C101.759 1.29293 98.6174 -2.55706e-05 95.3438 3.79286e-10H45.2883C42.0219 3.79286e-10 38.8658 1.30215 36.5484 3.59746L26.043 14.1029H0V70.5367H14.125C18.0094 70.5367 21.1434 67.4027 21.1654 63.5404H23.1738L41.8453 80.4021C48.665 85.9418 58.1332 86.0742 65.1516 81.2408C67.9104 83.6244 70.8898 84.75 74.2225 84.75C78.2393 84.75 82.0133 83.1168 84.9928 79.4531C89.8703 81.3732 95.6307 80.027 99.1178 75.7453L104.9 68.6166C106.136 67.0937 106.909 65.3502 107.306 63.5404H120.085C120.107 67.4027 123.263 70.5367 127.125 70.5367H141.25V14.1029H114.589ZM10.5938 63.4742C8.65156 63.4742 7.0625 61.8852 7.0625 59.943C7.0625 58.0008 8.65156 56.4117 10.5938 56.4117C12.5359 56.4117 14.125 58.0008 14.125 59.943C14.125 61.9072 12.5359 63.4742 10.5938 63.4742ZM96.668 61.9514L90.9076 69.058C90.2896 69.8084 89.1861 69.9408 88.4137 69.3229L83.1389 65.0412L76.5178 73.0969C75.1936 74.708 73.2072 74.1563 72.5451 73.6266L64.4232 66.6744L60.9803 70.9119C57.9125 74.6859 52.3287 75.2598 48.7754 72.3686L27.301 52.9467H21.1875V24.6746H30.435L44.0523 11.0793C44.4938 10.9027 44.8689 10.7482 45.3104 10.5717H57.8242L49.283 18.4066C42.7943 24.3436 42.4191 34.3635 48.3119 40.7639C51.5783 44.3393 61.8189 49.8568 70.7133 41.735L72.523 40.0797L96.4031 59.4574C97.1535 60.0754 97.2639 61.201 96.668 61.9514V61.9514ZM120.062 52.9467H104.79C104.282 52.3287 103.708 51.7549 103.09 51.2473L80.4242 32.8406L83.183 30.3246C84.6176 29.0004 84.7279 26.7713 83.4037 25.3367L80.998 22.7545C79.6738 21.3199 77.4447 21.2316 76.0102 22.5338L63.8273 33.7014C61.7307 35.6215 58.1553 35.776 56.191 33.7014C54.1385 31.5164 54.315 28.1617 56.4559 26.2195L70.934 12.9553C72.5672 11.4545 74.6859 10.6379 76.893 10.6379L95.3658 10.5938C95.8293 10.5938 96.2707 10.7703 96.5797 11.1014L110.197 24.6967H120.062V52.9467V52.9467ZM130.656 63.4742C128.714 63.4742 127.125 61.8852 127.125 59.943C127.125 58.0008 128.714 56.4117 130.656 56.4117C132.598 56.4117 134.188 58.0008 134.188 59.943C134.188 61.9072 132.598 63.4742 130.656 63.4742Z"
                      fill="#E8C893"
                    />
                  </svg>
                </div>
              </div>
              <div className="text-column">
                <p>
                  KLiKK X kan generere opp mot 25.000 ulike annonser i jakten på
                  interessenter. Flere interessenter kan gi flere budgivere og
                  høyere salgssum.
                </p>
                <div className="svg-icon center">
                  <svg
                    width="113.33"
                    height="85"
                    viewBox="0 0 136 102"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M31.2508 56.7508C32.9109 58.4109 35.6017 58.4109 37.2619 56.7508L51 43.0153L73.4958 65.5111C75.1559 67.1713 77.8467 67.1713 79.5069 65.5111L108.784 36.2312L116.617 44.0672C120.634 48.0834 127.5 45.2386 127.5 39.5595V12.75C127.5 10.4019 125.598 8.5 123.25 8.5H96.4405C90.7614 8.5 87.9166 15.3664 91.9328 19.3827L99.7688 27.2159L76.5 50.4847L54.0042 27.9889C52.3441 26.3287 49.6533 26.3287 47.9931 27.9889L28.2466 47.7355C26.5864 49.3956 26.5864 52.0864 28.2466 53.7466L31.2508 56.7508V56.7508ZM131.75 89.25H12.75V4.25C12.75 1.90187 10.8481 0 8.5 0H4.25C1.90187 0 0 1.90187 0 4.25V93.5C0 98.1936 3.80641 102 8.5 102H131.75C134.098 102 136 100.098 136 97.75V93.5C136 91.1519 134.098 89.25 131.75 89.25Z"
                      fill="#E8C893"
                    />
                  </svg>
                </div>
              </div>
            </Texts>
            <Texts>
              <div className="text-column">
                <p>
                  Annonseringen begrenses ikke til et fåtall nettsider.
                  Tjenesten tilpasser selv annonseformatet, og markedsfører
                  eiendommen der det gir best resultat, være seg Instagram
                  Stories eller Google.
                </p>
                <div className="svg-icon center">
                  <svg
                    width="133"
                    height="133"
                    viewBox="0 0 133 133"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M81.6247 24.28L88.0359 38.6276L63.3304 63.3331C61.658 65.0054 61.658 67.716 63.3304 69.3857C64.1652 70.2205 65.2623 70.6406 66.3567 70.6406C67.4511 70.6406 68.5482 70.2232 69.383 69.3857L94.0885 44.6802L108.436 51.0914C109.405 51.4151 110.445 51.4624 111.44 51.228C112.434 50.9935 113.343 50.4867 114.066 49.7642L131.103 32.7275C133.992 29.8376 132.831 24.9142 128.951 23.6191L114.058 18.6556L109.094 3.76483C108.297 1.3593 106.105 1.80925e-06 103.863 1.80925e-06C102.488 1.80925e-06 101.091 0.513752 99.9913 1.6135L82.9546 18.6502C82.2326 19.3729 81.7258 20.2822 81.4909 21.2765C81.2561 22.2707 81.3024 23.3106 81.6247 24.28V24.28ZM102.544 11.1714L105.942 21.3634L107.296 25.4253L111.358 26.7792L121.55 30.1775L109.528 42.1997L96.3923 36.3318L90.5244 23.1964L102.544 11.1714V11.1714ZM129.623 46.3178L122.618 53.323C123.589 57.5213 124.156 61.8721 124.156 66.3594C124.156 98.2279 98.2279 124.156 66.3594 124.156C34.4908 124.156 8.5625 98.2279 8.5625 66.3594C8.5625 34.4908 34.4908 8.5625 66.3594 8.5625C70.8493 8.5625 75.2002 9.12709 79.3958 10.1011L86.401 3.09588C79.9187 1.04258 73.1591 -0.00158649 66.3594 1.80925e-06C29.7092 1.80925e-06 0 29.7092 0 66.3594C0 103.01 29.7092 132.719 66.3594 132.719C103.01 132.719 132.719 103.01 132.719 66.3594C132.719 59.3729 131.624 52.6433 129.623 46.3178ZM73.5037 26.9853C73.0867 25.7368 72.8454 24.4364 72.7866 23.1214C70.6727 22.811 68.5615 22.4766 66.3594 22.4766C42.1034 22.4766 22.4766 42.1061 22.4766 66.3594C22.4766 90.6153 42.1061 110.242 66.3594 110.242C90.6153 110.242 110.242 90.6127 110.242 66.3594C110.242 64.1251 109.908 61.9845 109.587 59.8412C108.278 59.785 106.975 59.6298 105.731 59.215C105.164 59.0251 106.004 59.3809 100.195 56.7881C101.064 59.8519 101.68 63.02 101.68 66.3594C101.68 85.8364 85.8364 101.68 66.3594 101.68C46.8824 101.68 31.0391 85.8364 31.0391 66.3594C31.0391 46.8824 46.8824 31.0391 66.3594 31.0391C69.6987 31.0391 72.8669 31.6545 75.9306 32.5241C73.3378 26.7177 73.6937 27.5579 73.5037 26.9853V26.9853ZM66.3594 44.9531C54.5565 44.9531 44.9531 54.5565 44.9531 66.3594C44.9531 78.1622 54.5565 87.7656 66.3594 87.7656C78.1622 87.7656 87.7656 78.1622 87.7656 66.3594C87.7656 65.3506 87.6024 64.3847 87.4686 63.4133L75.441 75.441L75.4383 75.4383C73.1104 77.7636 69.8994 79.2031 66.3594 79.2031C59.2766 79.2031 53.5156 73.4422 53.5156 66.3594C53.5156 62.8193 54.9552 59.6084 57.2805 57.2805L57.2778 57.2778L69.3054 45.2501C68.3341 45.1163 67.3681 44.9531 66.3594 44.9531Z"
                      fill="#E8C893"
                    />
                  </svg>
                </div>
              </div>
            </Texts>
            <Texts>
              <div className="text-column">
                <p style={{ textAlign: 'center' }}>
                  PrivatMegleren har enerett i Norge på bruk av denne
                  teknologien. – Spør oss gjerne om KLiKK X.
                </p>
              </div>
            </Texts>
            <ButtonGroup style={{ justifyContent: 'center' }}>
              <Button type="primary" onClick={readMoreClick} colorTheme="gray">
                Les mer
              </Button>
            </ButtonGroup>
          </div>
        </Section>
        <Section ref={contactRef}>
          <FormWrapper style={fadeOut}>
            <Form onSubmit={handleSubmit} noValidate>
              <Heading tag="h2" center={true}>
                Tilbud på eiendomssalg
              </Heading>
              <p>
                Ønsker du å vite mer om KLiKK og eiendomssalg gjennom
                PrivatMegleren, kan du bestille et uforpliktende tilbud her.
                Fyll ut skjema, så tar en av våre meglere kontakt med deg.
              </p>
              {error &&
                error.graphQLErrors.map((error, index) => (
                  <FormError key={`error_${index}`} message={error.message} />
                ))}
              {fields.map((item: any, index: number) => {
                return (
                  <FormRow key={`field_${index}`}>
                    <Input
                      type={item.type}
                      name={item.name}
                      label={item.label}
                      labelAfter={item.labelAfter}
                      placeholder={item.placeholder}
                      value={item.value}
                      onChange={handleChange}
                      error={item.error}
                    />
                  </FormRow>
                );
              })}
              <ButtonGroup>
                <Button
                  type="primary"
                  colorTheme="gold"
                  disabled={loading}
                  loading={loading}
                >
                  Kontakt meg
                </Button>
                <ButtonLink
                  as="a"
                  href={routeConfig.personvern.url}
                  className="privacy-link-mobile"
                >
                  Personvernpolicy
                </ButtonLink>
              </ButtonGroup>
            </Form>
          </FormWrapper>
          <Thanks style={fadeIn}>
            <Section className="form-section">
              <Heading tag="h2" center={true}>
                Tusen takk
              </Heading>
              <Paragraph center={true}>
                En eiendomsmegler vil ta kontakt med deg så snart som mulig.
              </Paragraph>
              <Paragraph center={true}>
                I mellomtiden kan du lese mer om salg av eiendom her.
              </Paragraph>
              <AccordionGeneral />
            </Section>
            <Section className="form-section" style={{ paddingTop: 0 }}>
              <AccordionBenefits />
            </Section>
          </Thanks>
        </Section>
      </Container>

      <Section className="section">
        <Modal
          id="portals"
          noBodyScroll={true}
          showModal={showVideo}
          overlay={true}
          showCloseButton={true}
          onClickClose={() => setShowVideo(false)}
          onClickOutside={() => setShowVideo(false)}
        >
          <VideoPlayer
            type="vimeo"
            url="https://player.vimeo.com/video/386709023?autoplay=1"
          />
        </Modal>
        <ButtonGroup className="center" style={{ marginTop: '20px' }}>
          <PlayButton
            label="Se brandvideo"
            onClick={() => setShowVideo(true)}
          />
        </ButtonGroup>
      </Section>
      <KlikkPdf>
        <a href="https://privatmegleren.no/docs/klikk.pdf" target="_blank">
          <img
            alt="klikk-bilde"
            style={{ width: '100%' }}
            src="https://cdn.reeltime.no/pm_assets/klikk_assets/img/4029075e5308bc7ae67516ced8054246.png"
          />
        </a>
      </KlikkPdf>

      <WhiteContainer>
        <Section style={{ maxWidth: '960px', margin: '0 auto' }}>
          <h2 style={{ color: '#000' }} ref={readMoreRef}>
            Komplisert, men enkelt sagt - KLiKK X
          </h2>
          <p className="ingress">
            Mens vi jobber, leter vi alltid etter bedre måter å utføre arbeidet
            vårt på. Vi tror på å skape nye og smartere løsninger. Vi gjør dette
            for å la drømmer gå i oppfyllelse - for å sikre at noen oppdager den
            perfekte eiendommen og andre oppnår den høyeste salgssummen.
          </p>
          <Texts>
            <Col>
              <p className="left" style={{ color: 'black' }}>
                <b>EKSTREM PRESISJON</b>
                <br />
                Vår livssituasjon avgjør om vi er potensielle eiendomskjøpere.
                Har vi fått samboer, skal vi ha barn - eller har eneboligen
                blitt for stor? Er drømmen trappefri adkomst eller barnevennlig
                beliggenhet?
              </p>
              <p className="left" style={{ color: 'black' }}>
                Eiendomsannonsen skal ikke nå alle, den skal kun fange
                interessen til potensielle kjøpere. For å sikre at riktig person
                reagerer på riktig annonse til riktig tid, kan KLiKK X
                skreddersy inntil 25.000 ulike annonser for hver eiendom.
              </p>
              <p className="left" style={{ color: 'black' }}>
                <b>SELVFORBEDRENDE</b>
                <br />
                Smart eiendomsannonsering handler ikke lenger om cookies og
                A/B-testing (2). KLiKK X er bygget på siste generasjons
                annonseteknologi. Den benytter kunstig intelligens og
                maskinlæring – noe som gjør den bedre for hver eiendom den
                annonserer.
              </p>
              <p className="left" style={{ color: 'black' }}>
                Tjenesten er heller ikke låst til utvalgte nettsider. KLiKK X
                tilpasser annonseformatet (3) og annonserer der resultatet blir
                best - være seg Instagram Stories eller Google. Dette gir langt
                bedre effekt enn ordinær digital annonsering – opp mot 100
                ganger. Målinger viser at riktig annonse på riktig sted, gjør at
                eiendommens salgstid reduseres vesentlig (4).
              </p>
              <p className="left" style={{ color: 'black' }}>
                <b>EN HJELPENDE HÅND</b>
                <br />
                De fleste aktive eiendomssøkere på FINN.no legger inn søk med
                preferanser på hva de ser etter. Det betyr at disse
                eiendomskjøperne kun vil se de eiendommer som passer eksakt de
                kriterier de har lagt til grunn for søket. Det kan være alt fra
                pris, beliggenhet, størrelse, fasiliteter som balkong, antall
                soverom, garasje, utsikt med mer.
              </p>
            </Col>
            <Col>
              <p className="left" style={{ color: 'black' }}>
                I realiteten er det slik at man ofte kan vurdere å gi slipp på
                noen av kriteriene man har, dersom noe annet kan veie opp for
                det. KLiKK X utvider horisonten, og foreslår også de eiendommer
                som ligger tett opp mot det du søker etter, i tilfellet det kan
                være aktuelt.
              </p>
              <p className="left" style={{ color: 'black' }}>
                Hvis man for eksempel har satt en prisgrense på 4,5 millioner,
                så kan systemet likevel vise en eiendom til 5,5 millioner, fordi
                den matcher på samtlige av de andre kriteriene som er satt i
                søket. Dette kjøpet kan plutselig være aktuelt fordi det
                fnansielle kan løses ved for eksempel utleie. Dette er et
                eksempel på hvordan KLiKK X når langt flere kjøpere.
              </p>
              <p className="left" style={{ color: 'black' }}>
                <b>BRUKERVENNLIG</b>
                <br />
                Den nye versjonen av KLiKK X hjelper også megler til å
                optimalisere andre deler av salgsprosessen. Tjenesten viser
                hvilken tekst og bilder som gir best respons, slik at dette kan
                benyttes for bedre effekt i andre kanaler, som feks. på FINN.no.
                Megler får også beskjed når noen kommenterer annonser i sosiale
                medier - alle kommentarer vil håndteres.
              </p>
              <p className="left" style={{ color: 'black' }}>
                Kanskje er KLiKK X verdens mest avanserte eiendomsannonse?
                Nettopp derfor har vi vært opptatt av å kunne tilby en
                oversiktlig dashbord løsning hvor megler og eiendomsselgere
                enkelt kan følge annonseringen.
              </p>
            </Col>
          </Texts>
        </Section>
        <Section style={{ maxWidth: '960px', margin: '0 auto' }}>
          <Texts>
            <div
              className="text-column padding-bottom center"
              style={{ width: '100%' }}
            >
              <img
                src="https://cdn.reeltime.no/pm_assets/klikk_assets/images/klikk_fig_1v2.png"
                style={{ width: '100%' }}
              />
            </div>
            <div
              className="text-column padding-bottom center"
              style={{ width: '100%' }}
            >
              <img
                src="https://cdn.reeltime.no/pm_assets/klikk_assets/images/klikk_fig_2v2.png"
                style={{ width: '100%' }}
              />
            </div>
            <div className="text-column center" style={{ width: '100%' }}>
              <img
                src="https://cdn.reeltime.no/pm_assets/klikk_assets/images/klikk_fig_3v2.png"
                style={{ width: '100%' }}
              />
            </div>
          </Texts>
          <Section style={{ maxWidth: '960px', margin: '0 auto' }}>
            <Littom>
              <p
                className="left"
                style={{
                  display: 'block',
                  width: '100%',
                  color: 'black',
                  fontSize: '14px',
                  marginBottom: 0
                }}
              >
                <b>LITT MER OM KLIKK</b>
              </p>
              <ol
                style={{
                  listStyle: 'decimal',
                  padding: 0,
                  width: '100%',
                  display: 'block',
                  float: 'left',
                  color: 'black'
                }}
              >
                <li style={{ color: 'black', fontSize: '14px' }}>
                  Omnibuss gjennomført 17.-21. juni 2019 viser at andelen som
                  oppgir å være aktiv eiendomskjøper ligger mellom 5-9%, mens
                  antall passive er 28-30%. En passiv eiendomskjøper har ingen
                  konkrete planer om å kjøpe eiendom, men ville vurdert det,
                  dersom de kom over en interessant eiendom.
                </li>
                <li style={{ color: 'black', fontSize: '14px' }}>
                  {' '}
                  Cookies, også kalt informasjonskapsler, er små filer som
                  lagres på en brukers enhet i det brukeren besøker en nettside.
                  Annonsører kan benytte denne informasjonen i markedsføringen.
                  A/B-testing er en enkel testing av to ulike variantene av en
                  annonse som vises om hverandre, for å avgjøre hvilken variant
                  som skaffer flest klikk KLiKK X er i stand til å tilpasse
                  annonsens format for best mulig effekt. Den justerer høyde og
                  bredde, samt genererer video der dette formatet gir
                </li>
                <li style={{ color: 'black', fontSize: '14px' }}>
                  A/B-testing er en enkel testing av to ulike variantene av en
                  annonse som vises om hverandre, for å avgjøre hvilken variant
                  som skaffer flest klikk KLiKK X er i stand til å tilpasse
                  annonsens format for best mulig effekt. Den justerer høyde og
                  bredde, samt genererer video der dette formatet gir best
                  effekt.
                </li>
                <li style={{ color: 'black', fontSize: '14px' }}>
                  En digital annonse klikkes på i snitt 0,01% av visningene.
                  Tall fra kampanjer kjørt i 2019 viser at i snitt velger 11% å
                  trykke seg inn på eiendomsannonser fra KLiKK. Målinger gjort
                  ved salg av eiendommer i PrivatMegleren viser også at
                  eiendommer annonsert på FINN.no og KLiKK X, i snitt selges 27%
                  raskere enn eiendommer som kun annonseres på FINN.no.
                </li>
              </ol>
            </Littom>
          </Section>
        </Section>
      </WhiteContainer>

      <div
        className="section"
        style={{ background: 'white', padding: '70px 0' }}
      >
        <div className="container"></div>
        <div className="container center">
          <ButtonGroup style={{ justifyContent: 'center' }}>
            <Button type="primary" onClick={contactClick} colorTheme="gray">
              Kontakt oss
            </Button>
          </ButtonGroup>
        </div>
      </div>
    </>
  );
};

export default Klikk;

const KlikkX = styled.div`
  width: 100%;
  float: left;
  margin-bottom: 30px;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-pack: center;
  -ms-flex-pack: center;
  justify-content: center;
  text {
    font-family: DomaineDisp;
    font-size: 380px;
    font-weight: bold;
  }
  flex-wrap: wrap;

  @media (min-width: 800px) {
    flex-wrap: nowrap;
  }
`;

const FakeGraph = styled.div`
  text-align: center;
  width: 100%;
`;

const Texts = styled.div`
  font-family: 'Proxima-Nova', arial;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  .text-column {
    text-align: center;
    line-height: 1.6;
    padding: 0 20px 30px;
  }

  img {
    max-width: 250px;
  }
  @media (min-width: 800px) {
    flex-wrap: nowrap;
  }
`;

const WhiteContainer = styled.div`
  background-color: #fff;
  padding: 70px 30px;
  font-family: 'Proxima-Nova', sans-serif;

  .ingress {
    font-weight: bold;
    font-size: 20px;
    line-height: 1.6;
    color: #000;
  }
  @media (min-width: 800px) {
    h1 {
      padding: 0 20px;
    }
    .ingress {
      padding: 0 20px;
    }
  }
`;

const Col = styled.div`
  width: 100%;
  @media (min-width: 800px) {
    width: 50%;
    padding: 0 20px;
    line-height: 1.6;
  }
`;

const Littom = styled.div`
  ol {
    list-style: decimal;
    padding: 0;
    width: 100%;
    display: block;
    float: left;
    color: black;
  }
  li {
    color: black;
    font-size: 14px;
    margin: 1em !important;
    line-height: 1.6;
  }
`;

const KlikkPdf = styled(Section)`
  width: 100%;
  background: rgb(34, 34, 34);
  opacity: 1;
  padding: 96px 0;
  display: flex;
  justify-content: center;
`;

const FormWrapper = styled(animated.div)``;
